import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import HomePage from './pages/home/HomePage';
import AppLayout from './components/layout/AppLayout';
import AuthInit from './components/layout/AuthInit';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import { ToastProvider, ToastContainer } from './components/common/Toast';
import { GeolocationProvider } from './context/GeolocationContext';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Lazy-loaded components for code splitting
const NotFound = lazy(() => import('./pages/NotFound'));
const NotificationPrompt = lazy(() => import('./components/notifications/NotificationPrompt'));
const AppointmentDetailsPage = lazy(() => import('./pages/AppointmentDetailsPage'));
const ProviderPage = lazy(() => import('./pages/provider/ProviderPage'));
const AllAwardsPage = lazy(() => import('./pages/provider/AllAwardsPage'));
const AllEducationPage = lazy(() => import('./pages/provider/AllEducationPage'));
const AllServicesPage = lazy(() => import('./pages/provider/AllServicesPage'));
const ProviderListPage = lazy(() => import('./pages/ProviderListPage'));
const Login = lazy(() => import('./pages/auth/Login'));
const Register = lazy(() => import('./pages/auth/Register'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const AccountRecovery = lazy(() => import('./pages/auth/AccountRecovery'));
const ProviderOnboarding = lazy(() => import('./pages/auth/ProviderOnboarding'));
const Profile = lazy(() => import('./pages/profile/Profile'));
const Settings = lazy(() => import('./pages/settings/Settings'));
const BookingListPage = lazy(() => import('./pages/BookingListPage'));
const About = lazy(() => import('./pages/About'));
const FAQ = lazy(() => import('./pages/FAQ'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const ShippingPolicy = lazy(() => import('./pages/ShippingPolicy'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const CancellationRefundPolicy = lazy(() => import('./pages/CancellationRefundPolicy'));
const BecomeProviderPage = lazy(() => import('./pages/BecomeProviderPage'));
const BookingLookup = lazy(() => import('./pages/BookingLookup'));

// Route-level loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <GeolocationProvider>
        <ToastProvider>
          <Router>
            <ScrollToTop />
            <AuthInit>
              <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route element={<AppLayout />}>
                  <Route path="/provider/:providerId" element={<ProviderPage />} />
                  <Route path="/provider/:providerId/awards" element={<AllAwardsPage />} />
                  <Route path="/provider/:providerId/education" element={<AllEducationPage />} />
                  <Route path="/provider/:providerId/services" element={<AllServicesPage />} />
                  <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                  <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                  <Route path="/bookings" element={<ProtectedRoute><BookingListPage /></ProtectedRoute>} />
                  <Route path="/appointment/:id" element={<ProtectedRoute><AppointmentDetailsPage /></ProtectedRoute>} />
                  <Route path="/providers" element={<ProviderListPage />} />
                </Route>
                <Route path="/" element={<HomePage />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/list-a-business" element={<BecomeProviderPage />} />
                <Route path="/become-a-provider" element={<ProviderOnboarding />} />
                <Route path="/provider-signup" element={<ProviderOnboarding />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />
                <Route path="/account-recovery" element={<AccountRecovery />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact-us" element={<ContactUs />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                <Route path="/shipping-policy" element={<ShippingPolicy />} />
                <Route path="/cancellation-and-refund-policy" element={<CancellationRefundPolicy />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/booking-lookup" element={<BookingLookup />} />
                <Route path="/track-booking" element={<BookingLookup />} />
                {/* 404 Catch-all route - must be last */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
              <ToastContainer />
              <Suspense fallback={null}>
                <NotificationPrompt />
              </Suspense>
            </AuthInit>
          </Router>
        </ToastProvider>
      </GeolocationProvider>
    </ErrorBoundary>
  );
}

export default App;
